import { c as _c } from "react/compiler-runtime";
import { Language } from 'prism-react-renderer';
import { useContext, useMemo } from 'react';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { DEFAULT_API_VERSION, HookdeckAPIError } from '../../../../client/Hookdeck';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useAuthorized } from '../../../contexts/TeamPermissionContext';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
export type OutpostExampleLanguage = 'http' | 'typescript' | 'python' | 'go';
export interface OutpostExampleTab {
  key: OutpostExampleLanguage;
  label: string;
}
export interface OutpostExampleMap {
  http: string | null;
  typescript: string | null;
  python: string | null;
  go: string | null;
}
interface DestinationExampleInput {
  type: string;
  topics: '*' | string[];
  config: Record<string, unknown>;
  credentials?: Record<string, unknown>;
}
interface PublishEventExampleInput {
  id?: string;
  tenant_id: string;
  topic?: string;
  metadata?: Record<string, unknown>;
  data: unknown;
}
interface TenantExampleInput {
  tenant_id: string;
  metadata?: Record<string, unknown>;
}
export const OUTPOST_BASE_URL = `${process.env.OUTPOST_API_URL}/${DEFAULT_API_VERSION}` as string;
export const OUTPOST_EXAMPLE_TABS: OutpostExampleTab[] = [{
  label: 'REST API',
  key: 'http'
}, {
  label: 'TypeScript',
  key: 'typescript'
}, {
  label: 'Python',
  key: 'python'
}, {
  label: 'Go',
  key: 'go'
}];
const INSTALL_COMMANDS: OutpostExampleMap = {
  http: null,
  typescript: 'npm install @hookdeck/outpost-sdk',
  python: 'pip install outpost_sdk',
  go: 'go get github.com/hookdeck/outpost/sdks/outpost-go'
};
const INIT_EXAMPLES: OutpostExampleMap = {
  http: null,
  typescript: `import { Outpost } from '@hookdeck/outpost-sdk';

const outpost = new Outpost({
  apiKey: 'YOUR_API_KEY',
});`,
  python: `from outpost_sdk import Outpost

outpost = Outpost(api_key='YOUR_API_KEY')`,
  go: `import (
  outpostgo "github.com/hookdeck/outpost/sdks/outpost-go"
)

client := outpostgo.New(
  outpostgo.WithSecurity("YOUR_API_KEY")
)`
};
const shell_escape_single_quote = (value: string) => value.replace(/'/g, `'\"'\"'`);
const GO_RAW_STRING_DELIMITER = '`';
const getObjectEntries = (value: Record<string, unknown>) => Object.entries(value);
const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isIdentifier = (value: string) => /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(value);
const indent = (depth: number) => '  '.repeat(depth);
const toTypeScriptLiteral = (value: unknown, depth = 0): string => {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }
    const items = value.map(item => `${indent(depth + 1)}${toTypeScriptLiteral(item, depth + 1)}`);
    return `[\n${items.join(',\n')}\n${indent(depth)}]`;
  }
  if (isPlainObject(value)) {
    const entries = getObjectEntries(value);
    if (entries.length === 0) {
      return '{}';
    }
    const items = entries.map(([key, item]) => {
      const rendered_key = isIdentifier(key) ? key : JSON.stringify(key);
      return `${indent(depth + 1)}${rendered_key}: ${toTypeScriptLiteral(item, depth + 1)}`;
    });
    return `{\n${items.join(',\n')}\n${indent(depth)}}`;
  }
  return JSON.stringify(value);
};
const toPythonLiteral = (value: unknown, depth = 0): string => {
  if (value === null) {
    return 'None';
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'None';
  }
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }
    const items = value.map(item => `${indent(depth + 1)}${toPythonLiteral(item, depth + 1)}`);
    return `[\n${items.join(',\n')}\n${indent(depth)}]`;
  }
  if (isPlainObject(value)) {
    const entries = getObjectEntries(value);
    if (entries.length === 0) {
      return '{}';
    }
    const items = entries.map(([key, item]) => {
      return `${indent(depth + 1)}${JSON.stringify(key)}: ${toPythonLiteral(item, depth + 1)}`;
    });
    return `{\n${items.join(',\n')}\n${indent(depth)}}`;
  }
  return JSON.stringify(String(value));
};
const toGoLiteral = (value: unknown, depth = 0): string => {
  if (value === null) {
    return 'nil';
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'nil';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]interface{}{}';
    }
    const items = value.map(item => `${indent(depth + 1)}${toGoLiteral(item, depth + 1)}`);
    return `[]interface{}{\n${items.join(',\n')}\n${indent(depth)}}`;
  }
  if (isPlainObject(value)) {
    const entries = getObjectEntries(value);
    if (entries.length === 0) {
      return 'map[string]interface{}{}';
    }
    const items = entries.map(([key, item]) => {
      return `${indent(depth + 1)}${JSON.stringify(key)}: ${toGoLiteral(item, depth + 1)}`;
    });
    return `map[string]interface{}{\n${items.join(',\n')}\n${indent(depth)}}`;
  }
  return JSON.stringify(String(value));
};
const toJsonLiteral = (value: unknown) => JSON.stringify(value, null, 2);
const toMetadataStringRecord = (metadata: Record<string, unknown>) => Object.fromEntries(Object.entries(metadata).map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)]));
const buildDestinationGoSdkSnippet = (tenant_id: string, destination_input: DestinationExampleInput): string => {
  const payload = {
    type: destination_input.type,
    topics: destination_input.topics,
    config: destination_input.config,
    ...(destination_input.credentials ? {
      credentials: destination_input.credentials
    } : {})
  };
  return `payload := []byte(${GO_RAW_STRING_DELIMITER}${toJsonLiteral(payload)}${GO_RAW_STRING_DELIMITER})

var destination components.DestinationCreate
if err := json.Unmarshal(payload, &destination); err != nil {
  panic(err)
}

response, err := client.Destinations.Create(
  context.Background(),
  ${JSON.stringify(tenant_id)},
  destination,
)
if err != nil {
  panic(err)
}
_ = response`;
};
export const getOutpostInstallCommand = (language: OutpostExampleLanguage) => INSTALL_COMMANDS[language];
export const getOutpostInitExample = (language: OutpostExampleLanguage) => INIT_EXAMPLES[language];
export const getOutpostSnippetLanguage = (language: OutpostExampleLanguage): Language => {
  switch (language) {
    case 'http':
      return 'bash';
    case 'typescript':
      return 'typescript';
    case 'python':
      return 'python';
    case 'go':
    default:
      return 'go';
  }
};
export const obfuscateOutpostApiKey = (api_key_value: string) => {
  if (api_key_value.length <= 8) {
    return `${api_key_value.slice(0, 1)}${'*'.repeat(Math.max(api_key_value.length - 2, 1))}${api_key_value.slice(-1)}`;
  }
  return `${api_key_value.slice(0, 4)}${'*'.repeat(api_key_value.length - 8)}${api_key_value.slice(-4)}`;
};
const isUnauthorizedError = (error: unknown) => error instanceof HookdeckAPIError && (error.status === 401 || error.status === 403);
export const useOutpostSnippetApiKey = () => {
  const $ = _c(20);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team
  } = useContext(DashboardContext);
  const can_reveal = useAuthorized("member");
  let t0;
  if ($[0] !== team) {
    t0 = team ? APIMethodKeys.teams.listApiKeys(team.id) : null;
    $[0] = team;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.teams) {
    t1 = () => HookdeckAPI.teams.listApiKeys();
    $[2] = HookdeckAPI.teams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      shouldRetryOnError: _temp
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: project_api_keys_data,
    error: api_key_error,
    mutate: mutate_api_keys,
    isLoading
  } = useSWR(t0, t1, t2);
  let t3;
  if ($[5] !== project_api_keys_data) {
    t3 = project_api_keys_data ?? [];
    $[5] = project_api_keys_data;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const project_api_keys = t3;
  const api_key = project_api_keys[0];
  let t4;
  if ($[7] !== api_key_error) {
    t4 = isUnauthorizedError(api_key_error);
    $[7] = api_key_error;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== api_key || $[10] !== can_reveal) {
    t5 = api_key?.key ? {
      placeholder: "YOUR_API_KEY",
      masked_value: obfuscateOutpostApiKey(api_key.key),
      full_value: api_key.key,
      reveal_on_hover: can_reveal
    } : undefined;
    $[9] = api_key;
    $[10] = can_reveal;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== api_key || $[13] !== api_key_error || $[14] !== isLoading || $[15] !== mutate_api_keys || $[16] !== project_api_keys || $[17] !== t4 || $[18] !== t5) {
    t6 = {
      api_key,
      api_keys: project_api_keys,
      api_key_error,
      mutate_api_key: mutate_api_keys,
      mutate_api_keys,
      is_loading: isLoading,
      is_unauthorized: t4,
      maskable_value: t5
    };
    $[12] = api_key;
    $[13] = api_key_error;
    $[14] = isLoading;
    $[15] = mutate_api_keys;
    $[16] = project_api_keys;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
export const useOutpostSnippetMaskableValue = () => {
  return useOutpostSnippetApiKey().maskable_value;
};
export const buildCreateTenantExamples = ({
  tenant_id,
  metadata
}: TenantExampleInput): OutpostExampleMap => {
  const has_metadata = metadata && Object.keys(metadata).length > 0;
  const tenant_body = has_metadata ? {
    metadata
  } : undefined;
  const go_metadata = has_metadata ? toMetadataStringRecord(metadata) : undefined;
  return {
    http: has_metadata ? `curl -X PUT "${OUTPOST_BASE_URL}/tenants/${tenant_id}" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${shell_escape_single_quote(toJsonLiteral(tenant_body))}'` : `curl -X PUT "${OUTPOST_BASE_URL}/tenants/${tenant_id}" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    typescript: has_metadata ? `await outpost.tenants.upsert(${JSON.stringify(tenant_id)}, ${toTypeScriptLiteral(tenant_body)});` : `await outpost.tenants.upsert(${JSON.stringify(tenant_id)});`,
    python: has_metadata ? `outpost.tenants.upsert(tenant_id=${JSON.stringify(tenant_id)}, body=${toPythonLiteral(tenant_body)})` : `outpost.tenants.upsert(tenant_id=${JSON.stringify(tenant_id)})`,
    go: has_metadata ? `client.Tenants.Upsert(
  context.Background(),
  ${JSON.stringify(tenant_id)},
  &components.TenantUpsert{
    Metadata: ${toGoLiteral(go_metadata, 2).replace('map[string]interface{}', 'map[string]string')},
  },
)` : `client.Tenants.Upsert(context.Background(), ${JSON.stringify(tenant_id)}, nil)`
  };
};
export const buildCreateDestinationExamples = (tenant_id: string, destination_input: DestinationExampleInput): OutpostExampleMap => {
  const payload = {
    type: destination_input.type,
    topics: destination_input.topics,
    config: destination_input.config,
    ...(destination_input.credentials ? {
      credentials: destination_input.credentials
    } : {})
  };
  const go_sdk_snippet = buildDestinationGoSdkSnippet(tenant_id, destination_input);
  return {
    http: `curl -X POST "${OUTPOST_BASE_URL}/tenants/${tenant_id}/destinations" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${shell_escape_single_quote(toJsonLiteral(payload))}'`,
    typescript: `await outpost.destinations.create(${JSON.stringify(tenant_id)}, ${toTypeScriptLiteral(payload)});`,
    python: `outpost.destinations.create(
  tenant_id=${JSON.stringify(tenant_id)},
  body=${toPythonLiteral(payload)}
)`,
    go: go_sdk_snippet
  };
};
export const buildPublishEventExamples = ({
  id,
  tenant_id,
  topic,
  metadata,
  data
}: PublishEventExampleInput): OutpostExampleMap => {
  const has_metadata = metadata && Object.keys(metadata).length > 0;
  const request_body = {
    ...(id ? {
      id
    } : {}),
    tenant_id,
    ...(topic ? {
      topic
    } : {}),
    ...(has_metadata ? {
      metadata
    } : {}),
    data
  };
  const typescript_payload = {
    ...(id ? {
      id
    } : {}),
    tenantId: tenant_id,
    ...(topic ? {
      topic
    } : {}),
    ...(has_metadata ? {
      metadata
    } : {}),
    data
  };
  return {
    http: `curl -X POST "${OUTPOST_BASE_URL}/publish" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${shell_escape_single_quote(toJsonLiteral(request_body))}'`,
    typescript: `await outpost.publish(${toTypeScriptLiteral(typescript_payload)});`,
    python: `outpost.publish(${toPythonLiteral(request_body)})`,
    go: `client.Publish(context.Background(), components.PublishRequest{
  ${id ? `ID:       outpostgo.Pointer(${JSON.stringify(id)}),\n  ` : ''}TenantID: outpostgo.Pointer(${JSON.stringify(tenant_id)}),
  ${topic ? `Topic:    outpostgo.Pointer(${JSON.stringify(topic)}),\n  ` : ''}${has_metadata ? `Metadata: ${toGoLiteral(metadata, 1)},\n  ` : ''}Data:     ${toGoLiteral(data, 1)},
})`
  };
};
function _temp(error) {
  return !isUnauthorizedError(error);
}